/**
 * SQL Server user-defined type context menu (Programmability -> Types).
 *
 * Covers the three leaf kinds: table types, alias ("user-defined data") types
 * and CLR types. Mirrors the routine menu's Script-as actions minus ALTER —
 * SQL Server has no `ALTER TYPE` for any of them.
 */

import { ContextMenuAction } from '../../explorer/models/TreeModel';
import { DatabaseObjectTreeNode, DatabaseObjectNodeMetadata } from '../../explorer/models/DatabaseTreeModels';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { useWorkspaceStore } from '../../workspace/core/store/workspaceStore';
import { parseNodeId } from '../../explorer/utils/nodeIdUtils';
import type { DatabaseObjectType } from '../models/databaseObjectTypes';
import type { MssqlUserTypeKind } from './mssqlUserTypeScript';
import { openObjectScriptTab } from './objectScriptTab';

/** Object kinds this menu owns. */
export const USER_TYPE_OBJECT_TYPES: readonly DatabaseObjectType[] = [
  'table-type',
  'alias-type',
  'clr-type',
];

export function isUserTypeObjectType(type: string): type is MssqlUserTypeKind {
  return (USER_TYPE_OBJECT_TYPES as readonly string[]).includes(type);
}

const LABELS: Record<MssqlUserTypeKind, string> = {
  'table-type': 'table type',
  'alias-type': 'data type',
  'clr-type': 'CLR type',
};

export function generateUserTypeContextMenuActions(kind: MssqlUserTypeKind): ContextMenuAction[] {
  const label = LABELS[kind];
  return [
    { id: 'copy-name', label: `Copy ${label} name`, icon: 'clipboard' },
    { id: 'open-definition', label: 'Open definition', icon: 'code' },
    // Flat CREATE / DROP rather than a "Script … as" submenu: SQL Server has no
    // ALTER TYPE, so there is no third item to justify nesting — the same shape
    // `generateOracleObjectContextMenuActions` uses for Oracle types.
    { id: 'script-create', label: 'Script as CREATE', icon: 'file-plus' },
    { id: 'script-drop', label: 'Script as DROP', icon: 'trash' },
    { id: 'separator', label: '', isSeparator: true },
    { id: 'refresh', label: 'Refresh', icon: 'refresh' },
  ];
}

/** Folder id shape: `<kind>-types-folder<->connectionId<->database[<->schema]`. */
function extractDatabaseFromParentId(parentId: string | null | undefined): string | undefined {
  if (!parentId) return undefined;
  const parts = parseNodeId(parentId);
  return parts.length >= 3 ? parts[2] : undefined;
}

function trackScriptAction(kind: MssqlUserTypeKind, action: 'create' | 'drop' | 'open-definition'): void {
  window.electronAPI?.analytics?.track('feature:explorer_script_type', { kind, action }).catch(() => {});
}

export function executeUserTypeContextMenuAction(
  actionId: string,
  nodeId: string,
  handlers: { onRefresh?: (nodeId: string) => void } = {},
  node?: DatabaseObjectTreeNode,
): void {
  if (actionId === 'refresh') {
    handlers.onRefresh?.(nodeId);
    return;
  }

  if (!node) {
    toast.error({ title: 'Error', description: 'Node not found' });
    return;
  }

  const { objectDetails } = node.metadata as DatabaseObjectNodeMetadata;
  const { name, schema, connectionId, type } = objectDetails;
  if (!isUserTypeObjectType(type)) return;

  if (actionId === 'copy-name') {
    void writeClipboardText(name);
    toast.success({ title: 'Copied', description: `${name} copied to clipboard` });
    return;
  }

  // Open definition shows the same generated CREATE — a type has no separate
  // stored source to read back.
  const mode = actionId === 'script-drop' ? 'drop' : 'create';
  if (actionId !== 'script-create' && actionId !== 'script-drop' && actionId !== 'open-definition') {
    return;
  }
  trackScriptAction(type, actionId === 'open-definition' ? 'open-definition' : mode);

  const { activeConnection } = getConnectionById(connectionId);
  const database =
    extractDatabaseFromParentId(node.parentId) ||
    (objectDetails.metadata?.database as string | undefined) ||
    activeConnection?.connection.database ||
    '';

  const tabId = openObjectScriptTab(
    connectionId,
    database,
    name,
    `-- Loading ${mode.toUpperCase()} script for ${schema}.${name}...\n`,
    mode.toUpperCase(),
    true,
  );
  if (!tabId) return;

  void (async () => {
    const { updateTabContent } = useWorkspaceStore.getState();
    try {
      const { fetchMssqlUserTypeScript } = await import('./mssqlUserTypeScriptService');
      const { script, error } = await fetchMssqlUserTypeScript({
        connectionId,
        database,
        schema,
        name,
        kind: type,
        mode,
      });
      updateTabContent(tabId, { query: script, isGeneratingScript: false });
      if (error) {
        toast.error({ title: 'Script Error', description: error });
      }
    } catch (error) {
      updateTabContent(tabId, {
        query: `-- Error: ${error instanceof Error ? error.message : 'Unknown error'}\n`,
        isGeneratingScript: false,
      });
    }
  })();
}
